import React from 'react';

import {
  type InfiniteListFetcherProps,
  InfiniteListFetcherRTK,
} from 'app/general/datafetchers/InfiniteListFetcherRTK';
import {
  convertLocationParamsToFetchParams,
  useLazyUsersQuery,
} from 'app/user/api';
import {gt, ngt} from 'app/utils/i18n';

import type {UsersFetchParamsType} from 'app/user/types';
import type {
  BasicUserSerializedType,
  UserSerializedType,
  UserTeamSerializedType,
} from 'sweet-types';

export function convertToBasicUser(
  user: UserSerializedType
): BasicUserSerializedType {
  return {
    id: user.id,
    name: user.name,
    url: user.url,
    //html_url: user.html_url,
    email: user.email,
    avatar_image: user.avatar_image,
    is_deleted: user.is_deleted,
    content_type: 'user',
  };
}

export function getUserAccountRelation(
  user: UserSerializedType,
  accountOwnerEmail?: string
): {
  name: string;
  super: boolean;
  regular: boolean;
} {
  if (user.email === accountOwnerEmail) {
    return {
      name: gt('Account Owner'),
      super: true,
      regular: false,
    };
  } else if (user.is_super_manager) {
    return {
      name: gt('Super Manager'),
      super: true,
      regular: false,
    };
  } else if (user.is_super_teammate) {
    return {
      name: gt('Super Teammate'),
      super: true,
      regular: false,
    };
  } else if (user.is_manager) {
    return {
      name: ngt(
        'Manager in %s team',
        'Manager in %s teams',
        categorizeMemberships(user.team_memberships).manager.length
      ),
      super: false,
      regular: false,
    };
  }
  return {
    name: gt('Regular member'),
    super: false,
    regular: true,
  };
}

export const categorizeMemberships = (
  memberships: UserSerializedType['team_memberships']
): {member: Array<string>; manager: Array<string>} =>
  memberships.reduce(
    (relations, membership) => {
      if (membership.is_manager) {
        relations.manager.push(membership.team);
      } else {
        relations.member.push(membership.team);
      }
      return relations;
    },
    {manager: [], member: []} as {member: Array<string>; manager: Array<string>}
  );

export function getCategorizedUserTeams(user: UserSerializedType): {
  manager: Array<UserTeamSerializedType>;
  member: Array<UserTeamSerializedType>;
} {
  const teamsMap = user.teams.reduce((map, team) => {
    map[team.url] = team;
    return map;
  }, {} as {[url: string]: UserTeamSerializedType});

  const memberships = categorizeMemberships(user.team_memberships);

  return {
    member: memberships.member.map((url) => teamsMap[url]),
    manager: memberships.manager.map((url) => teamsMap[url]),
  };
}

export const UsersInfiniteListFetcherRTK = (
  props: InfiniteListFetcherProps<UserSerializedType>
) => (
  <InfiniteListFetcherRTK<UserSerializedType, UsersFetchParamsType>
    {...props}
    useLazyDataHook={useLazyUsersQuery}
    paramsConverter={convertLocationParamsToFetchParams}
  />
);
